import { useMemoizedFn } from 'ahooks';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import useOperateDPSReport from './useOperateDPSReport';
import { getInitialDPSReport } from '../services';
import { addUuidBatch, getFirstDayOfMonth } from '@gp/utils/common';
import { getInitialWeekDate } from '../../utils';
import { getCalendarEndDate } from '../utils';
import { setNewFileUrlChanges } from '../components/UpdatesOfWeek/utils';
import type { IDateRange } from '@home/types/common';
import type { IDPSReportFilter } from '@gp/types/dpsReport/common';
import type { IDPSReportVOV3 } from '@gp/types/dpsReport/v3';
import type { IDPSReportFormValue } from '../types';

export default function useAddDPSReportNew(props?: {
  disabledWeeks?: string[][];
  // 获取当前周报的value
  getReport?: () => IDPSReportFormValue;
  // 设置当前周报的value
  setReport?: (payload: Partial<IDPSReportFormValue>) => void;
  // 初始化
  initialize?: (payload: IDPSReportVOV3) => void;
}) {
  const { disabledWeeks, getReport, setReport, initialize } = props || {};

  const [reportParams, setReportParams] = useState({});

  const updateReportPage = useMemoizedFn(async (filter: IDPSReportFilter, calendarsDates: IDateRange) => {
    try {
      setIsLoading(true);
      const res = await getInitialDPSReport(filter, calendarsDates);
      const { changes, calendar: calendarsData } = res || {};
      const changesValue = setNewFileUrlChanges(changes);

      const { list: calendars } = calendarsData || {};
      const reportTmp = {
        comment: '',
        project_comment: [],
        changes: addUuidBatch(changesValue),
        memo: [],
        calendar: {
          ...calendarsDates,
          list: addUuidBatch(calendars),
        },
      };
      setReportParams(reportTmp);
      setReport?.(reportTmp);
      // @ts-ignore
      initialize?.(reportTmp);
    } finally {
      setIsLoading(false);
    }
  });

  const handleFilterChange = useMemoizedFn((payload: IDPSReportFilter) => {
    const filterTmp = { ...filter, ...payload };
    const { start_date: filterStartDateTmp } = filterTmp || {};
    const calendarsDatesTmp = {
      start_date: getFirstDayOfMonth(filterStartDateTmp),
      end_date: getCalendarEndDate(filterStartDateTmp),
    };
    const report = getReport?.() || {};
    // @ts-ignore
    const reportCalendars = report?.calendar || {};
    const reportCalendarsTmp = { ...reportCalendars, ...calendarsDatesTmp };
    const reportTmp = { ...report, calendar: reportCalendarsTmp };
    setFilter(filterTmp);
    setReport?.(reportTmp);
    updateReport(filterTmp, calendarsDatesTmp);
  });

  const getInitialFilter = useMemoizedFn(() => getInitialWeekDate(disabledWeeks));

  const {
    isGuide,
    isLoading,
    isCalendarInnerLoading,
    setIsCalendarInnerLoading,
    setIsLoading,
    isSubmitting,
    submitType,
    reportHelper,
    projects,
    filter,
    setFilter,
    updateReport,
    onCalendarRefreshData,
    onSubmit,
  } = useOperateDPSReport({
    pageId: 'WORKSPACE_DPS_WEEKLY_REPORT_ADD',
    confirmBtnId: 'WORKSPACE_DPS_WEEKLY_REPORT_ADD_CONFIRM_SUBMIT',
    publishBtnId: 'WORKSPACE_DPS_WEEKLY_REPORT_ADD_PUBLISH_SUBMIT',
    refreshCalendarBtnId: 'WORKSPACE_DPS_WEEKLY_REPORT_ADD_REFRESH_CALENDAR_SUBMIT',
    getInitialFilter,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    updateReportPage,
    getReport,
    setReport,
  });

  return {
    isGuide,
    isLoading,
    isCalendarInnerLoading,
    setIsCalendarInnerLoading,
    isSubmitting,
    submitType,
    reportHelper,
    projects,
    filter,
    onFilterChange: handleFilterChange,
    onCalendarRefreshData,
    onSubmit,
    reportParams,
  };
}
